import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Container, Box, Typography, Button, IconButton, Dialog } from '@mui/material';
import { Instagram } from '@mui/icons-material';
import Header from './components/Header';
import Home from './pages/Home';
import Products from './pages/Products';
import Cart from './components/Cart';
import ProductDetail from './pages/ProductDetail';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import LegalNotice from './pages/LegalNotice';
import OrderConfirmation from './pages/OrderConfirmation';
import Contact from './pages/Contact';
import Wishlist from './pages/Wishlist';
import Account from './pages/Account';
import Admin from './pages/Admin';
import ReviewPage from './pages/ReviewPage';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import { ProductsProvider } from './context/ProductsContext';
import { AuthProvider } from './context/AuthContext';
import snapchatLogo from './pages/snapchat-logo.png';
import tiktokLogo from './pages/tik tok-logo.png';
import whatsappLogo from './pages/whatsapp-logo.png';
import PaymentMethodsLogos from './components/PaymentMethodsLogos';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#262626',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#757575',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f2f2f2',
      paper: '#ffffff',
    },
    text: {
      primary: '#111111',
      secondary: '#5f6368',
    },
    divider: '#e0e0e0',
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.5px',
    },
    h2: {
      fontWeight: 600,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          padding: '8px 20px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow:
              '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
          },
        },
      },
    },
  },
});

function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch (e) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  return null;
}

function App() {
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [showWhatsAppQr, setShowWhatsAppQr] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('cookie_consent');
      if (!stored) {
        setShowCookieBanner(true);
      }
    } catch (e) {
      setShowCookieBanner(true);
    }
  }, []);

  const handleAcceptCookies = () => {
    try {
      localStorage.setItem('cookie_consent', 'accepted');
    } catch (e) {
      // ignore
    }
    setShowCookieBanner(false);
  };

  const handleRefuseCookies = () => {
    try {
      localStorage.setItem('cookie_consent', 'rejected');
    } catch (e) {
      // ignore
    }
    setShowCookieBanner(false);
  };

  return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <ProductsProvider>
            <WishlistProvider>
              <CartProvider>
                <Router>
                  <ScrollToTop />
                <div className="App">
              <Header />
              <main>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/products/:id" element={<ProductDetail />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/wishlist" element={<Wishlist />} />
                  <Route path="/account" element={<Account />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/review/:token" element={<ReviewPage />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-conditions" element={<TermsConditions />} />
                  <Route path="/legal-notice" element={<LegalNotice />} />
                  <Route path="/order-confirmation" element={<OrderConfirmation />} />
                </Routes>
              </main>

              {showCookieBanner && (
                <Box
                  sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    bgcolor: 'background.paper',
                    boxShadow: 3,
                    py: 2,
                    zIndex: 1300,
                  }}
                >
                  <Container maxWidth="lg">
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { md: 'center' },
                        gap: 2,
                      }}
                    >
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Gestion des cookies
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Nous utilisons des cookies pour améliorer votre expérience et analyser le
                          trafic. Vous pouvez accepter ou refuser les cookies. Consultez notre{' '}
                          <Button
                            component={Link}
                            to="/privacy-policy"
                            color="inherit"
                            size="small"
                          >
                            Politique de confidentialité
                          </Button>{' '}
                          et nos{' '}
                          <Button
                            component={Link}
                            to="/terms-conditions"
                            color="inherit"
                            size="small"
                          >
                            Conditions générales
                          </Button>
                          .
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button variant="text" color="inherit" onClick={handleRefuseCookies}>
                          Refuser
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleAcceptCookies}
                        >
                          Accepter
                        </Button>
                      </Box>
                    </Box>
                  </Container>
                </Box>
              )}
              <footer
                style={{
                  marginTop: '4rem',
                  padding: '2rem 0',
                  backgroundColor: theme.palette.background.default,
                }}
              >
                <Container maxWidth="lg">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="flex-start"
                    flexDirection={{ xs: 'column', md: 'row' }}
                    gap={4}
                    color="text.secondary"
                    sx={{ textAlign: { xs: 'center', md: 'center' } }}
                  >
                    <Box sx={{ maxWidth: 320, mx: 'auto' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                        <PaymentMethodsLogos />
                      </Box>
                    </Box>
                    <Box sx={{ maxWidth: 320, mx: 'auto' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Liens utiles
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Button
                          component={Link}
                          to="/products"
                          color="inherit"
                          size="small"
                          sx={{ justifyContent: 'center', mx: 'auto' }}
                        >
                          Produits
                        </Button>
                        <Button
                          component={Link}
                          to="/contact"
                          color="inherit"
                          size="small"
                          sx={{ justifyContent: 'center', mx: 'auto' }}
                        >
                          Nous contacter
                        </Button>
                        <Button
                          component={Link}
                          to="/legal-notice"
                          color="inherit"
                          size="small"
                          sx={{ justifyContent: 'center', mx: 'auto' }}
                        >
                          Mentions légales
                        </Button>
                        <Button
                          component={Link}
                          to="/privacy-policy"
                          color="inherit"
                          size="small"
                          sx={{ justifyContent: 'center', mx: 'auto' }}
                        >
                          Politique de confidentialité
                        </Button>
                        <Button
                          component={Link}
                          to="/terms-conditions"
                          color="inherit"
                          size="small"
                          sx={{ justifyContent: 'center', mx: 'auto' }}
                        >
                          Conditions générales
                        </Button>
                      </Box>
                    </Box>
                    <Box sx={{ maxWidth: 320, mx: 'auto' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Nous suivre
                      </Typography>
                      <Box display="flex" justifyContent="center" gap={1} mb={1}>
                        <IconButton
                          color="inherit"
                          size="small"
                          aria-label="Instagram"
                          href="https://www.instagram.com/bratan.family/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Instagram />
                        </IconButton>
                        <IconButton
                          color="inherit"
                          size="small"
                          aria-label="Snapchat"
                          href="https://www.snapchat.com/add/bratanfamily45"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Box
                            component="img"
                            src={snapchatLogo}
                            alt="Snapchat"
                            sx={{ width: 24, height: 24, borderRadius: '50%' }}
                          />
                        </IconButton>
                        <IconButton
                          color="inherit"
                          size="small"
                          aria-label="TikTok"
                          href="https://www.tiktok.com/@bratan.family"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Box
                            component="img"
                            src={tiktokLogo}
                            alt="TikTok"
                            sx={{ width: 24, height: 24, borderRadius: '50%' }}
                          />
                        </IconButton>
                        <IconButton
                          color="inherit"
                          size="small"
                          aria-label="WhatsApp"
                          onClick={() => setShowWhatsAppQr(true)}
                        >
                          <Box
                            component="img"
                            src={whatsappLogo}
                            alt="WhatsApp"
                            sx={{ width: 24, height: 24, borderRadius: '50%' }}
                          />
                        </IconButton>
                      </Box>
                      <Typography variant="body2">
                        Email : bratanfamily59@gmail.com
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Téléphone via WhatsApp : +32 456 96 20 71
                      </Typography>
                    </Box>
                  </Box>
                  <Box mt={3} textAlign="center">
                    <Typography variant="body2">
                      © {new Date().getFullYear()} Boutique de Vêtements. Tous droits réservés.
                    </Typography>
                  </Box>
                </Container>
              </footer>

              <Dialog
                open={showWhatsAppQr}
                onClose={() => setShowWhatsAppQr(false)}
                maxWidth="xs"
                fullWidth
              >
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Ajouter Bratan Family sur WhatsApp
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Scannez ce QR code avec votre application WhatsApp pour ouvrir directement
                    notre profil et discuter avec nous.
                  </Typography>
                  <Box
                    component="img"
                    src="/qr-w.jpg"
                    alt="QR code WhatsApp Bratan Family"
                    sx={{
                      width: '100%',
                      maxWidth: 320,
                      mx: 'auto',
                      display: 'block',
                      borderRadius: 2,
                      mt: 2,
                    }}
                  />
                  <Box sx={{ textAlign: 'right', mt: 3 }}>
                    <Button onClick={() => setShowWhatsAppQr(false)}>Fermer</Button>
                  </Box>
                </Box>
              </Dialog>
                </div>
              </Router>
            </CartProvider>
          </WishlistProvider>
        </ProductsProvider>
        </AuthProvider>
      </ThemeProvider>
  );
}

export default App;